import { Close } from '@emotion-icons/material/Close'
import styled from '@emotion/styled'
import { IconButton, Modal, Typography } from '@mui/material'
import { Suspense } from 'react'
import { AlphamapLogoThrobber } from 'src/components/alphamap-logo/throbber'
import { FlexColumn } from 'src/components/flex-containers'
import { Colors, Shapes } from 'src/components/theme'
import { FANCY_SCROLLBAR } from 'src/utils/styles'
import { SidebarResultOverlayHeaderLoading } from 'src/v2/components/skeleton-loaders'

const ModalContent = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: ${Colors.bgWhite};
  border: 1px solid ${Colors.border};
  border-radius: ${Shapes.borderRadius}px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`
const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: ${Shapes.spacing[4]}px;
  border-bottom: 1px solid ${Colors.border};
`
const ModalHeaderText = styled.div`
  display: flex;
  align-items: center;
  gap: ${Shapes.spacing[2]}px;
`
const ModalBody = styled.div`
  flex: 1;
  padding: ${Shapes.spacing[4]}px;
  background-color: ${Colors.neutral50};
  overflow-y: auto;
  ${FANCY_SCROLLBAR}
`
const Subtitle = styled(Typography)`
  text-transform: capitalize;
`

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
`

type GenericModalProps = {
  open: boolean
  onClose: () => void
  height?: number
  width?: number
  icon?: React.ReactNode
  title?: string
  subtitle?: string
  children: React.ReactNode
  headerLoading?: boolean
}
export const GenericModal = (props: GenericModalProps) => {
  const { open, onClose, height = 500, width = 500, icon, title, subtitle, children, headerLoading } = props

  const onCloseModal = (_: any, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return
    }
    onClose()
  }

  return (
    <Modal open={open} onClose={onCloseModal}>
      <ModalContent
        style={{
          height: `${height}px`,
          width: `${width}px`,
        }}>
        <ModalHeader>
          {headerLoading ? (
            <SidebarResultOverlayHeaderLoading />
          ) : (
            <>
              <ModalHeaderText>
                {icon && <div>{icon}</div>}
                <FlexColumn gap={Shapes.spacing[1]}>
                  {title && (
                    <Typography variant="header3" color={Colors.neutral950}>
                      {title}
                    </Typography>
                  )}
                  {subtitle && (
                    <Subtitle variant="textmdm" color={Colors.neutral500}>
                      {subtitle}
                    </Subtitle>
                  )}
                </FlexColumn>
              </ModalHeaderText>
              <IconButton onClick={onClose}>
                <Close size={16} color={Colors.neutral400} />
              </IconButton>
            </>
          )}
        </ModalHeader>
        <ModalBody>
          <Suspense
            fallback={
              <LoaderWrapper>
                <AlphamapLogoThrobber />
              </LoaderWrapper>
            }>
            {children}
          </Suspense>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
